import { Entity, PostData, TargetedCountries } from '@/types/postData';
import { EntityDetails, EntityTypes } from '@/types/entities';
import { EnrichedOrganisationGeotargetingDto, EnrichedStudyGeotargetingDto } from '@studyportals/campaign-management-api-interface';
import { DegreeLevelPresenter } from '@studyportals/campaign-management-api-interface';

class EntityDetailsHandler {
	public targetingGroups: TargetedCountries[] = [];
	public pricingGroups: string[] = [];
	private entityDegreeLevelPresenter: DegreeLevelPresenter;

	constructor() {
		this.entityDegreeLevelPresenter = new DegreeLevelPresenter();
	}

	public retrieveSelectedEntity(postData: PostData, selectedEntity: EntityDetails): Entity | undefined {
		const relevantStudy = postData.studies.find(
			(study) => study.id === selectedEntity.id && selectedEntity.type === EntityTypes.STUDY
		);
		if (relevantStudy) {
			return relevantStudy;
		}

		const relevantOrganisation = postData.organisations.find(
			(organisation) => organisation.id === selectedEntity.id && selectedEntity.type === EntityTypes.ORGANISATION
		);
		if (relevantOrganisation) {
			return relevantOrganisation;
		}
	}

	public prepareListingOverviewDetailsForAllEntities(postData: PostData): EntityDetails[] {
		const studies = postData.studies.map((study) =>
			this.prepareDetailsForSingleEntity(study, EntityTypes.STUDY)
		);
		const organisations = postData.organisations.map((organisation) =>
			this.prepareDetailsForSingleEntity(organisation, EntityTypes.ORGANISATION)
		);
		return organisations.concat(studies);
	}

	public prepareDetailsForAllEntities(
		studyMap: Map<number, EnrichedStudyGeotargetingDto>,
		organisationMap: Map<number, EnrichedOrganisationGeotargetingDto>
	): EntityDetails[] {
		this.targetingGroups = [];
		this.pricingGroups = [];
		const entities: EntityDetails[] = [];
		for (const study of studyMap.values()) {
			const studyDetails = this.prepareDetailsForStudyEntity(study);
			entities.push(studyDetails);
		}
		for (const organisation of organisationMap.values()) {
			const organisationDetails = this.prepareDetailsForOrganisationEntity(organisation);
			entities.push(organisationDetails);
		}

		return entities;
	}

	private prepareDetailsForOrganisationEntity(entity: EnrichedOrganisationGeotargetingDto): EntityDetails {
		return {
			id: entity.id,
			type: EntityTypes.ORGANISATION,
			degreeType: EntityTypes.ORGANISATION,
			title: entity.name || '',
			degreeName: '',
			targetingGroup: this.arrangeGroupForEntityTargeting(entity),
			pricingGroup: this.arrangeGroupForEntityPricing(entity)
		};
	}

	private prepareDetailsForStudyEntity(entity: EnrichedStudyGeotargetingDto): EntityDetails {
		return {
			id: entity.id,
			type: EntityTypes.STUDY,
			degreeType: entity.level,
			title: entity.name || '',
			degreeName: this.entityDegreeLevelPresenter.getAbbreviatedName(entity.degree || entity.level),
			targetingGroup: this.arrangeGroupForEntityTargeting(entity),
			pricingGroup: this.arrangeGroupForEntityPricing(entity)
		};
	}

	private prepareDetailsForSingleEntity(entity: Entity, type: EntityTypes): EntityDetails {
		let degreeType = '';
		if (type === EntityTypes.ORGANISATION) {
			degreeType = type;
		}
		
		return {
			id: entity.id,
			type,
			degreeType,
			title: entity.name ? entity.name : '',
			degreeName: '', 
			targetingGroup: this.arrangeGroupForEntityTargeting(entity),
			pricingGroup: this.arrangeGroupForEntityPricing(entity)
		};
	}

	private arrangeGroupForEntityTargeting(entity: Entity): number {
		const indexOfExistingGroup = this.findIndexOfGroupThatMatchesEntityTargeting(entity);
		if (indexOfExistingGroup === -1) {
			const indexOfNewGroup = this.targetingGroups.length;
			this.targetingGroups.push(entity.targeted_countries);
			return indexOfNewGroup;
		}

		return indexOfExistingGroup;
	}

	private arrangeGroupForEntityPricing(entity: Entity): number {
		const indexOfExistingGroup = this.findIndexOfGroupThatMatchesEntityPricing(entity);
		if (indexOfExistingGroup === -1 && entity.pricing !== undefined) {
			const indexOfNewGroup = this.pricingGroups.length;
			this.pricingGroups.push(entity.pricing);
			return indexOfNewGroup;
		}

		return indexOfExistingGroup;
	}

	private findIndexOfGroupThatMatchesEntityTargeting(entity: Entity): number {
		return this.targetingGroups.findIndex((group) => {
			for (const property in entity.targeted_countries) {
				const targetedCountry = property as keyof TargetedCountries;
				const specificGroup = group[targetedCountry];

				if (specificGroup.length !== entity.targeted_countries[targetedCountry].length) {
					return false;
				}
				const deviatingCountry = specificGroup.find(
					(country, index) => country !== entity.targeted_countries[targetedCountry][index]
				);

				if (deviatingCountry !== undefined) {
					return false;
				}
			}

			return true;
		});
	}

	private findIndexOfGroupThatMatchesEntityPricing(entity: Entity): number {
		return this.pricingGroups.findIndex((group) => {
			return group === entity.pricing;
		});
	}
}

export default new EntityDetailsHandler();
